import { useState } from 'react';
import { ListTree, ChevronRight, ChevronDown, Copy } from 'lucide-react';
import { APPROVER_PALETTE } from './absenceApprovalRulesSnapshot';

const TYPE_META = {
  applicationRole: { label: 'Application Role', cls: 'bg-teal-100 text-teal-800 border-teal-200' },
  approvalGroups: { label: 'Approval Groups', cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  jobLevelLineManager: { label: 'Job-Level Line Manager', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  managementHierarchy: { label: 'Management Hierarchy', cls: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  positionHierarchy: { label: 'Position Hierarchy', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  representative: { label: 'Representative', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  selfAutoApprove: { label: 'Self Auto Approve', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  users: { label: 'Users', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

function typeMeta(type) {
  return TYPE_META[type] || { label: type, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
}

function SpecRow({ label, value, mono = false }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex gap-2">
      <dt className="w-36 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </dt>
      <dd className={`min-w-0 break-words text-[10px] text-slate-600 ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function ThenSpec({ spec, index }) {
  const autoEnabled =
    spec.autoActionEnabled === true
      ? 'True'
      : spec.autoActionEnabled === false
      ? 'False'
      : null;
  const p = spec.participants;
  return (
    <dl className="mt-1 space-y-0.5 rounded-md border border-slate-100 bg-slate-50/70 px-2 py-1.5">
      {index != null ? (
        <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Stage {index + 1}
        </div>
      ) : null}
      <SpecRow label="List Builder" value={spec.listBuilder} />
      <SpecRow label="Response Type" value={spec.responseType} />
      <SpecRow label="Number of levels" value={spec.levels != null ? String(spec.levels) : null} />
      <SpecRow label="Starting Participant" value={spec.startingParticipant} mono />
      <SpecRow label="Top Participant" value={spec.topParticipant} mono />
      {p ? (
        <>
          <SpecRow label="Participants · Users" value={p.users} mono />
          <SpecRow label="Participants · Groups" value={p.groups} mono />
          <SpecRow label="Participants · App Role" value={p.applicationRole} mono />
        </>
      ) : null}
      <SpecRow label="Auto Action Enabled" value={autoEnabled} />
      <SpecRow label="Auto Action" value={spec.autoAction} mono />
    </dl>
  );
}

function ThenSpecBlocks({ rule }) {
  if (Array.isArray(rule.thenStages) && rule.thenStages.length) {
    return (
      <div className="space-y-1">
        {rule.thenStages.map((s, i) => (
          <ThenSpec key={i} spec={s} index={i} />
        ))}
      </div>
    );
  }
  if (rule.thenSpec) return <ThenSpec spec={rule.thenSpec} />;
  return null;
}

function ApproverChip({ type }) {
  const m = typeMeta(type);
  return (
    <span className={`inline-flex shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function RuleCard({ rule, onCopyRule }) {
  const chain = rule.then || [];
  const ifLine = rule.ifDetail
    ? `absenceDispStatusCd = "${rule.ifDetail.statusCode}"${
        rule.ifDetail.absenceTypes?.length
          ? ` AND absenceType \u2208 { ${rule.ifDetail.absenceTypes.join(', ')} }`
          : ''
      }`
    : rule.ifText || null;

  return (
    <li className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 transition-colors hover:border-cyan-300 hover:bg-cyan-50/30">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
          <span
            className="inline-flex shrink-0 items-center rounded-md bg-slate-800 px-2 py-0.5 text-[11px] font-semibold text-white"
            title={rule.truncatedName ? 'Rule name truncated in source screenshot' : undefined}
          >
            {rule.name}
            {rule.truncatedName ? <span className="ml-0.5 text-slate-400">*</span> : null}
          </span>
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-300">then</span>
          {rule.thenUnknown || !chain.length ? (
            <span className="inline-flex rounded-md border border-dashed border-slate-300 px-2 py-0.5 text-[11px] italic text-slate-400">
              approvers not captured
            </span>
          ) : (
            chain.map((t, ti) => (
              <span key={ti} className="inline-flex items-center gap-1.5">
                {ti > 0 ? <ChevronRight className="h-3 w-3 text-slate-300" /> : null}
                <ApproverChip type={t} />
              </span>
            ))
          )}
        </div>
        {onCopyRule ? (
          <button
            type="button"
            onClick={() => onCopyRule(rule)}
            title="Copy this rule into the editor to modify and deploy"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan-200 bg-white px-2 py-1 text-[11px] font-semibold text-cyan-700 shadow-sm hover:bg-cyan-50"
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
        ) : null}
      </div>
      {ifLine ? (
        <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">
          <span className="font-bold uppercase tracking-wide text-slate-400">if </span>
          {ifLine}
        </p>
      ) : null}
      {rule.thenDetail ? (
        <p className="mt-0.5 text-[10px] leading-relaxed text-slate-400">{rule.thenDetail}</p>
      ) : null}
      <ThenSpecBlocks rule={rule} />
    </li>
  );
}

/**
 * Read-only viewer for a configured approval ruleset (rule name + THEN approver chain),
 * laid out like the BPM Worklist Assignees view. Data is a captured snapshot, not live.
 */
export default function BpmConfiguredRulesView({ ruleset, defaultOpen = true, onCopyRule }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!ruleset?.rules?.length) return null;

  const { task, rules } = ruleset;
  const anyTruncated = rules.some((r) => r.truncatedName);

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-200 px-5 py-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ListTree className="h-5 w-5 shrink-0 text-slate-500" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-800">
              Approval rule blueprints
            </h2>
            <p className="truncate text-xs text-slate-500">
              {`Examples to copy & adapt \u00b7 ${task?.name} \u00b7 ${rules.length} rules`}
            </p>
          </div>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="space-y-3 p-4">
          {/* Approver type legend */}
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600">
              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              Approver types
            </summary>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {APPROVER_PALETTE.map((p) => (
                <ApproverChip key={p.type} type={p.type} />
              ))}
            </div>
          </details>

          {/* Rule cards */}
          <ol className="space-y-1.5">
            {rules.map((rule, i) => (
              <RuleCard key={`${rule.name}-${i}`} rule={rule} onCopyRule={onCopyRule} />
            ))}
          </ol>

          {anyTruncated ? (
            <p className="text-[10px] text-slate-400">
              * Rule name truncated in the source capture — verify the full name in the tenant.
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
